import React from 'react';
import { Recipe } from '../../types';
import { useAppConfig } from '../../context/AppConfigContext';
import { formatNumber } from '../../utils/formatters';
import { useModalScrollLock } from './useModalScrollLock';

interface RecipeDetailModalProps {
  recipe: Recipe;
  onClose: () => void;
  isSelected?: boolean;
  onToggleSelected?: () => void;
}

export default function RecipeDetailModal({
  recipe,
  onClose,
  isSelected = false,
  onToggleSelected,
}: RecipeDetailModalProps) {
  const { t } = useAppConfig();
  useModalScrollLock();
  const imageUrl = recipe.imageUrl && recipe.imageUrl.startsWith('http') ? recipe.imageUrl : '';
  const title = recipe.proteinType
    ? t('recipeDetail.title', 'Receta de {protein}', {
        protein: String(recipe.proteinType).toLowerCase(),
      })
    : recipe.name;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4 overflow-hidden"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="bg-white rounded-2xl max-w-md w-full shadow-2xl max-h-[85vh] overflow-y-auto overscroll-contain"
        onClick={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        {/* Header con foto y cerrar */}
        <div className="relative bg-[#FAF7F1] px-6 py-5">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center text-xl text-gray-500 shadow-sm"
            aria-label={t('recipeDetail.close_aria', 'Cerrar')}
          >
            ×
          </button>
          <img src={imageUrl} alt={title} className="w-40 h-40 object-contain mx-auto" />
        </div>

        <div className="p-6 space-y-4">
          <h2 className="font-fredoka text-xl font-bold text-[#1a1a1a] text-center">{title}</h2>

          {recipe.ingredients && (
            <div>
              <h3 className="text-xs font-bold text-[#008BBC] uppercase tracking-wider mb-2">
                {t('recipeDetail.ingredients', 'Ingredientes')}
              </h3>
              <p className="text-sm text-[#666] leading-relaxed">
                {recipe.ingredients}
                {recipe.nutritionFacts?.moisture > 0 &&
                  ` ${t('recipeDetail.moisture_inline', 'Humedad: {pct}%.', {
                    pct: recipe.nutritionFacts.moisture,
                  })}`}
              </p>
            </div>
          )}

          <div className="bg-[#FAF7F1] rounded-xl p-4 space-y-2">
            <h3 className="text-xs font-bold text-[#008BBC] uppercase tracking-wider mb-2">
              {t('recipeDetail.nutrition_title', 'Información nutricional')}
            </h3>
            {recipe.kcalPer100g > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-[#888]">{t('recipeDetail.energy', 'Energía')}</span>
                <span className="font-semibold text-[#1a1a1a]">
                  {t('recipeDetail.kcal_per_100g', '{kcal} kcal/100g', { kcal: formatNumber(recipe.kcalPer100g) })}
                </span>
              </div>
            )}
            {recipe.nutritionFacts && (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-[#888]">{t('recipeDetail.protein', 'Proteína')}</span>
                  <span className="font-semibold text-[#1a1a1a]">{recipe.nutritionFacts.protein}%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#888]">{t('recipeDetail.fat', 'Grasa')}</span>
                  <span className="font-semibold text-[#1a1a1a]">{recipe.nutritionFacts.fat}%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#888]">{t('recipeDetail.fiber', 'Fibra')}</span>
                  <span className="font-semibold text-[#1a1a1a]">{recipe.nutritionFacts.fiber}%</span>
                </div>
                {recipe.nutritionFacts.moisture > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-[#888]">{t('recipeDetail.moisture', 'Humedad')}</span>
                    <span className="font-semibold text-[#1a1a1a]">{recipe.nutritionFacts.moisture}%</span>
                  </div>
                )}
              </>
            )}
          </div>

          {recipe.benefits && recipe.benefits.length > 0 && (
            <div>
              <h3 className="text-xs font-bold text-[#008BBC] uppercase tracking-wider mb-2">
                {t('recipeDetail.benefits', 'Beneficios')}
              </h3>
              <ul className="space-y-1.5">
                {recipe.benefits.map((b, i) => (
                  <li key={i} className="text-sm text-[#666] flex items-start gap-2">
                    <span className="text-[#4caf50] mt-0.5">✓</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-2">
            {onToggleSelected && (
              <button
                type="button"
                onClick={onToggleSelected}
                className={`w-full py-3 rounded-xl font-fredoka font-bold transition-colors ${
                  isSelected
                    ? 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'
                    : 'bg-[#008BBC] text-white hover:bg-[#007aa8]'
                }`}
              >
                {isSelected
                  ? t('recipeDetail.remove_from_plan', 'Quitar del plan')
                  : t('recipeDetail.add_to_plan', 'Añadir al plan')}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="w-full py-3 bg-[#1a1a1a] text-white font-bold rounded-xl hover:bg-black/80 transition-colors"
            >
              {t('recipeDetail.close', 'Cerrar')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
